// renderLatex.js
// Converts text with $...$ (inline) and $$...$$ (display) LaTeX into HTML via KaTeX.
// Also handles basic markdown: **bold**, *italic*, numbered/bulleted lists.
// Returns an HTML string safe for dangerouslySetInnerHTML.

import katex from "katex";
import "katex/dist/katex.min.css";

// Match $$...$$ first (display), then $...$ (inline)
const DISPLAY_RE = /\$\$([\s\S]+?)\$\$/g;
const INLINE_RE = /\$([^\n$]+?)\$/g;

function escapeHtml(str) {
  return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

export function renderLatex(text) {
  /* Math comes OUT before anything else escapes, and goes back in at the very end.
   *
   * The output of this function is handed to dangerouslySetInnerHTML, and its inputs are not
   * ours: homework problem text is extracted from a user's uploaded PDF by an LLM, and chat
   * replies are model output. Escaping first and rendering after does not work — KaTeX would
   * receive `&lt;` for a perfectly ordinary `x < 1` — so the math is lifted into placeholders,
   * the remaining prose is escaped, and the rendered KaTeX HTML is spliced back in.
   *
   * \u0000 as the delimiter because it cannot appear in the source text (JSON transport would
   * not survive it) and survives escapeHtml untouched.
   */
  const mathHtml = [];
  const stash = (html) => `\u0000M${mathHtml.push(html) - 1}\u0000`;

  // Display math $$...$$ first, so an inline pass cannot chew a $$ pair in half.
  let result = text.replace(DISPLAY_RE, (_, tex) => {
    try {
      return stash(katex.renderToString(tex.trim(), { displayMode: true, throwOnError: false }));
    } catch {
      return `$$${tex}$$`;
    }
  });

  result = result.replace(INLINE_RE, (_, tex) => {
    try {
      return stash(katex.renderToString(tex.trim(), { displayMode: false, throwOnError: false }));
    } catch {
      return `$${tex}$`;
    }
  });

  // Everything that is NOT math is prose, and prose is text, not markup.
  result = escapeHtml(result);

  // Markdown: **bold**
  result = result.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");

  // Markdown: *italic* (but not inside KaTeX spans)
  result = result.replace(/(?<!\w)\*([^*\n]+?)\*(?!\w)/g, "<em>$1</em>");

  // Convert lines into paragraphs, handling lists
  const lines = result.split("\n");
  const processed = [];
  let inList = false;
  let listType = null;

  for (const line of lines) {
    const trimmed = line.trim();

    // ATX heading: "# h1" through "###### h6". Rendered as a bold block rather than a real
    // <h*>: this output is dropped inside chat bubbles and cards, where a browser-default h3
    // arrives with a huge font-size and margins that dwarf the surrounding body text. Without
    // this branch the hashes survive to the screen as literal "### 1. The Geometry".
    const headingMatch = trimmed.match(/^#{1,6}\s+(.+)/);
    // Numbered list: "1. item" or "2. item"
    const numberedMatch = !headingMatch && trimmed.match(/^(\d+)\.\s+(.+)/);
    // Bulleted list: "- item" or "* item" (only at start of line)
    const bulletMatch = !headingMatch && !numberedMatch && trimmed.match(/^[-*]\s+(.+)/);

    if (headingMatch) {
      // A heading ends any run of list items above it, or the <li>s keep accumulating into a
      // list the heading is visually below.
      if (inList) {
        processed.push(listType === "ol" ? "</ol>" : "</ul>");
        inList = false;
        listType = null;
      }
      processed.push(
        `<div style="font-weight:700;margin:10px 0 2px">${headingMatch[1]}</div>`,
      );
    } else if (numberedMatch) {
      if (!inList || listType !== "ol") {
        if (inList) processed.push(listType === "ol" ? "</ol>" : "</ul>");
        processed.push('<ol style="margin:4px 0;padding-left:20px">');
        inList = true;
        listType = "ol";
      }
      processed.push(`<li>${numberedMatch[2]}</li>`);
    } else if (bulletMatch) {
      if (!inList || listType !== "ul") {
        if (inList) processed.push(listType === "ol" ? "</ol>" : "</ul>");
        processed.push('<ul style="margin:4px 0;padding-left:20px">');
        inList = true;
        listType = "ul";
      }
      processed.push(`<li>${bulletMatch[1]}</li>`);
    } else {
      if (inList) {
        processed.push(listType === "ol" ? "</ol>" : "</ul>");
        inList = false;
        listType = null;
      }
      if (trimmed === "") {
        processed.push("<br>");
      } else {
        processed.push(trimmed);
      }
    }
  }
  if (inList) {
    processed.push(listType === "ol" ? "</ol>" : "</ul>");
  }

  // Math back in, last: the placeholders have travelled through escaping, the markdown passes
  // and the line/list walk untouched, and what returns here is KaTeX's own HTML.
  return processed.join("\n").replace(/\u0000M(\d+)\u0000/g, (_, i) => mathHtml[Number(i)] ?? "");
}

/** Render maths in text that is NOT a title: a homework problem, a topic description.
 *
 * Deliberately not `safeRenderLatex` (utils/videoTitle), which runs `cleanTitle` first — that
 * keeps only the FIRST LINE and cuts at 120 characters. Correct for a video title; silently
 * destructive here, because real extracted homework is routinely longer than 120 chars and
 * often multi-part ("Part (a)... Part (b)..."). Measured before this existed: a two-part
 * problem lost part (b) entirely, and a one-sentence mechanics question lost its question.
 * Overflow is the CSS clamp's job — it hides the tail, it does not delete it.
 */
export function renderMath(text) {
  const str = String(text ?? "");
  try {
    return renderLatex(str);
  } catch {
    return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }
}
